// src/store/feedStore.ts
/**
 * Tu Metadata
 * @tuScore 0.82 (Orchestrator - complex notification logic + dormancy recovery)
 * @tuGrade B+
 * @tuRb 1.35 (handles 5+ notification types, realtime subscriptions, unread tracking, gap-filling)
 * @tuTime 3.5h (per-meeting unread tracking Dec 2025 + dormancy gap-fill Feb 2026)
 * @tuPattern per-entity-tracking, realtime-subscription, dormancy-recovery
 * @tuBaseline 0.75 (no dormancy recovery or gap-filling)
 * @tuROI +0.02/hr
 * @lines ~1400
 * @complexity 20
 * @lastUpdated 2026-07-17
 *
 * Help Context
 * @tuPurpose "Central store for notifications, unread tracking, feed events, and dormancy recovery"
 * @tuHelp "Shows notification count, tracks unread per-user and per-meeting, recovers missed events after sleep"
 * @tuUserAction "view-notifications, mark-as-read"
 * @tuRelated ["streamStore", "StandupListView", "PeopleAgentsListView", "useWakeRecovery"]
 * @tuPrereq "User must be logged in"
 * @tuOutcome "Unread indicators update in realtime, notifications appear after dormancy without refresh"
 *
 * Key Patterns:
 * - Per-Entity Tracking: O(1) lookup for unreadDirectMessages, unreadStandups, unreadTeams
 * - Gap-Filling: lastEventTimestamp tracked; refreshAfterWake fetches events since that timestamp
 * - Reconnection: refreshAfterWake tears down and re-subscribes the realtime channel
 */
/**
 * .tuMetrics {"tu1": 0.85, "tu2": 0.90, "tu3": 9, "notes": "Feed event store with dormancy gap-fill, unread tracking, and browser notifications", "tuVersion": "2.5"}
 * .help "Tracks feed events, unread counts, dispatches browser notifications, and recovers from dormancy."
 */
// 
// STANDARD NOTIFICATION SYSTEM
// ============================
// 
// Core Principle: Recipients get notifications, senders don't (except for system confirmations)
// 
// Notification Types:
// 1. Direct Notifications: userId = specific user ID (e.g., DM received, task assigned)
// 2. System Notifications: userId = current user (e.g., task completed by you)
// 3. Announcements: Individual notifications created for each user (except sender)
// 
// Filtering Logic:
// - Only show notifications where userId === currentUser.id
// - This ensures users only see notifications meant for them
// - Senders don't get notified about their own actions
// 
// Standard Patterns:
// - DM notifications: Only when recipient not actively viewing chat
// - Announcements: Individual notifications for all company users except sender
// - Task notifications: Only for assignee, not creator
// - Mention notifications: Only for mentioned users
//
// Current time: Friday, August 29, 2025 at 6:30:00 PM UTC
// Summary: Adds markStreamAsRead(streamType, recipientStreamId) for consistent unread clearing across DMs, Teams, Standups, and Company. Keeps markDirectMessagesAsRead for compatibility.

import { create } from 'zustand';
import { FeedEvent, Card, Sprint, Roadmap } from '../types';
import { supabase } from '../lib/supabase';
import useUserStore from './userStore';
import useSharedItemStore from './sharedItemStore';
import useCardStore from './cardStore';
import useSprintStore from './sprintStore';
import useRoadmapStore from './roadmapStore';
import useFollowStore from './followStore';
import useTeamStore from './teamStore';
import { RealtimeChannel } from '@supabase/supabase-js';
import { mapFeedEventFromDB, type FeedEventRow } from '../lib/supabase/queries/feed-queries';
import { twoHrefFromFeedEvent } from '../features/feed/twoHref';
import { isFeedEventForUser } from '../features/feed/feedVisibility';
import { eventBelongsToSelectedTeam } from './feed/teamFilter';
import { eventMatchesFollows, type FollowRef } from '../features/feed/followMatch';
import { unreadGroupCount } from '../features/stream/streamModel';

// Helper function to check if an event is read by a specific user
const isEventReadByUser = (event: FeedEvent, userId: string): boolean => {
  return event.read && event.read[userId] === true;
};

type StreamT = 'direct_message' | 'team_stream' | 'standup_stream' | 'company_stream';

interface FeedState {
  events: FeedEvent[];
  unreadCount: number;
  unreadDirectMessages: Record<string, boolean>;
  unreadStandups: Record<string, boolean>; // Per-meeting unread tracking (keyed by recipientStreamId)
  unreadTeams: Record<string, boolean>; // December 4, 2025: Per-team unread tracking (keyed by team id)
  unreadTeamsCount: number;
  announcementBanner: {
    message: string;
    type: 'announcement' | 'urgent';
    isVisible: boolean;
    initiatorId?: string;
  } | null;
  unreadStandupsCount: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMoreEvents: boolean;
  consecutiveEmptyLoads: number; // Track consecutive loads with no new user events
  error: string | null;
  realtimeChannel: RealtimeChannel | null;
  commentsRealtimeChannel: RealtimeChannel | null; // For unread indicator tracking
  lastEventTimestamp: string | null; // Track for gap-filling after dormancy
  teardownRealtime: () => void;

  initializeEvents: () => Promise<void>;
  refreshAfterWake: () => Promise<void>; // Gap-fill missed events + reconnect channel
  initializeUnreadIndicators: () => Promise<void>; // Calculate indicators from comments table
  loadMoreEvents: () => Promise<void>;
  addEvent: (event: Omit<FeedEvent, 'id' | 'createdAt' | 'read'> & {
    streamType?: FeedEvent['streamType'];
    recipientStreamId?: string;
    shared_item_id?: string;
  }) => Promise<void>;

  markAsRead: (eventId: string) => Promise<void>;
  markAllAsRead: (cardId?: string) => Promise<void>;

  // NEW: unified stream read API
  markStreamAsRead: (streamType: StreamT, recipientStreamId: string) => Promise<void>;
  // Kept for compatibility; now implemented via markStreamAsRead
  markDirectMessagesAsRead: (otherUserId: string) => Promise<void>;
  
  // Announcement banner
  showAnnouncementBanner: (message: string, type: 'announcement' | 'urgent', initiatorId?: string) => void;
  hideAnnouncementBanner: () => void;

  // OS-level notification for mentions/DMs (implemented below; was missing from this interface)
  showBrowserNotification: (event: FeedEvent) => void;

  getFilteredEvents: (
    filter: 'all' | 'collaborative' | 'following',
    cards: Card[],
    sprints: Sprint[],
    roadmaps: Roadmap[],
    follows: FollowRef[],
    selectedTeamId: string | null // September 26, 2025 2:30 PM: Added selectedTeamId parameter
  ) => FeedEvent[];
  getUnreadCountForFilter: (
    filter: 'all' | 'collaborative' | 'following',
    cards: Card[],
    sprints: Sprint[],
    roadmaps: Roadmap[],
    follows: FollowRef[],
    selectedTeamId: string | null // September 26, 2025 3:00 PM: Added selectedTeamId parameter
  ) => number;
}

const ALLOWED_FEED_EVENT_TYPES = new Set([
  'task_created',
  'task_updated',
  'task_completed',
  'task_completed_by_assignee', // New: Notify creator when assignee completes task
  'task_reminder', // December 5, 2025: Due date reminders
  'task_deleted',
  'task_blocked',
  'task_unblocked',
  // December 4, 2025: Added idea events (same treatment as tasks)
  'idea_proposed',
  'idea_updated',
  'idea_deleted',
  'dependency_added',
  'dependency_removed',
  'mention',
  'devbot_suggestion',
  'card_converted',
  'vote_up',
  'vote_down',
  'vote_changed',
  'vote_unvoted',
  'song_proposed',
  'song_selected',
  'github_connected',
  'github_pr_created',
  'github_pr_merged',
  'github_pr_ready_for_review', // New: PR ready for review (notify approvers)
    'github_commit_linked', // New: Commit auto-linked to card
    'github_branch_created', // New: Branch created from card
    'github_pr_status_changed', // New: PR status updated
    'github_issue_detected', // New: GitHub issue detected (PM Agent analyzes)
    'entity_followed',
  'comment_added',
  'comment_replied',
  'comment_mentioned',
  'direct_message_received',
  'stream_message_sent',
  'stream_message_mentioned',
  'shared_item_created',
  'requirement_created',
  'requirement_updated',
  'requirement_linked',
  'sprint_started',
  'sprint_completed',
  'sprint_paused',
  'sprint_resumed',
  'sprint_created',
  'sprint_deleted',
  'roadmap_created',
  'roadmap_updated',
  'roadmap_deleted',
  'approval_requested',
  'approval_approved',
  'approval_rejected',
  'announcement_made',
  'meet_started', // New: Google Meet started (notify participants)
  'plan_created',
  'plan_updated',
  'plan_participant_added',
  'plan_draft_updated',
  'plan_author_added',
  'time_tracking', // Time tracking start/pause/resume/stop on task cards
  'guide_published',
  'guide_draft_updated',
  'guide_author_added',
]);

// Lazy refs avoid a circular import (streamStore → feedStore). Populated on first
// initializeEvents / initializeUnreadIndicators so realtime handlers can sync-check.
type StreamStoreHook = typeof import('./streamStore').default;
type FloatingChatStoreHook = typeof import('./floatingChatStore').default;
let cachedStreamStore: StreamStoreHook | null = null;
let cachedFloatingChatStore: FloatingChatStoreHook | null = null;

function ensureConversationViewCaches(): void {
  if (!cachedStreamStore) {
    void import('./streamStore').then((m) => {
      cachedStreamStore = m.default;
    });
  }
  if (!cachedFloatingChatStore) {
    void import('./floatingChatStore').then((m) => {
      cachedFloatingChatStore = m.default;
    });
  }
}

/**
 * True when the user is already looking at this conversation (StreamPage or
 * open floating/gutter chat). Prevents sidebar dots from lighting for messages
 * that arrive while the room is open.
 *
 * @param streamType conversation stream type
 * @param conversationId for DMs: the other user's id; for team/standup: recipient_stream_id
 */
function isActivelyViewingConversation(
  streamType: string | undefined | null,
  conversationId: string | undefined | null,
): boolean {
  if (!streamType || !conversationId) return false;

  if (cachedStreamStore) {
    const stream = cachedStreamStore.getState();
    if (
      stream.activeStreamType === streamType &&
      stream.activeRecipientStreamId === conversationId
    ) {
      return true;
    }
  }

  if (cachedFloatingChatStore) {
    const floating = cachedFloatingChatStore.getState();
    if (
      floating.isOpen &&
      floating.activeStreamType === streamType &&
      floating.activeRecipientId === conversationId
    ) {
      return true;
    }
  }

  return false;
}

const useFeedStore = create<FeedState>((set, get) => ({
  events: [],
  unreadCount: 0,
  unreadDirectMessages: {},
  unreadStandups: {}, // Per-meeting unread tracking
  unreadTeams: {}, // December 4, 2025: Per-team unread tracking
  unreadTeamsCount: 0,
  unreadStandupsCount: 0,
  isLoading: false,
  isLoadingMore: false,
  hasMoreEvents: true,
  consecutiveEmptyLoads: 0,
  error: null,
  realtimeChannel: null,
  commentsRealtimeChannel: null,
  lastEventTimestamp: null,
  announcementBanner: null,

  teardownRealtime: () => {
    const { realtimeChannel, commentsRealtimeChannel } = get();
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
    }
    if (commentsRealtimeChannel) {
      supabase.removeChannel(commentsRealtimeChannel);
    }
    set({ realtimeChannel: null, commentsRealtimeChannel: null });
  },

  // February 2026: Gap-fill missed events + reconnect realtime channel after dormancy
  refreshAfterWake: async () => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) return;

    const lastTs = get().lastEventTimestamp;
    console.log('🔄 [FeedStore] refreshAfterWake — fetching events since:', lastTs || '(all)');

    try {
      let query = supabase
        .from('feed_events')
        .select('*')
        .eq('company_id', currentUser.companyId)
        .order('created_at', { ascending: false })
        .limit(200);

      if (lastTs) {
        query = query.gt('created_at', lastTs);
      }

      const { data, error } = await query;
      if (error) throw error;

      if (data && data.length > 0) {
        const mapped = data.map((row) => mapFeedEventFromDB(row as FeedEventRow));
        set((state) => {
          const existingIds = new Set(state.events.map((e) => e.id));
          const newEvents = mapped.filter(
            (e) => !existingIds.has(e.id) && isFeedEventForUser(e, currentUser.id),
          );
          if (newEvents.length === 0) return state;

          console.log(`🔄 [FeedStore] refreshAfterWake — found ${newEvents.length} missed events`);
          const merged = [...newEvents, ...state.events];
          const newestTs = merged[0]?.createdAt || state.lastEventTimestamp;

          let addUnread = 0;
          const nextUnreadDMs = { ...state.unreadDirectMessages };
          const nextUnreadTeams = { ...state.unreadTeams };
          const nextUnreadStandups = { ...state.unreadStandups };
          let addTeams = 0;
          let addStandups = 0;

          for (const evt of newEvents) {
            const isForMe = evt.userId === currentUser.id;
            const isRead = isEventReadByUser(evt, currentUser.id);
            if (isForMe && !isRead) {
              const viewingDm =
                evt.type === 'direct_message_received' &&
                !!evt.initiatorId &&
                isActivelyViewingConversation('direct_message', evt.initiatorId);
              const viewingTeam =
                evt.streamType === 'team_stream' &&
                isActivelyViewingConversation('team_stream', evt.recipientStreamId);
              const viewingStandup =
                evt.streamType === 'standup_stream' &&
                isActivelyViewingConversation('standup_stream', evt.recipientStreamId);
              if (viewingDm || viewingTeam || viewingStandup) continue;

              addUnread++;
              if (evt.type === 'direct_message_received' && evt.initiatorId) {
                nextUnreadDMs[evt.initiatorId] = true;
              }
              if (evt.streamType === 'team_stream') {
                addTeams++;
                if (evt.recipientStreamId) nextUnreadTeams[evt.recipientStreamId] = true;
              }
              if (evt.streamType === 'standup_stream') {
                addStandups++;
                if (evt.recipientStreamId) nextUnreadStandups[evt.recipientStreamId] = true;
              }
            }
          }

          return {
            events: merged,
            lastEventTimestamp: newestTs,
            unreadCount: state.unreadCount + addUnread,
            unreadDirectMessages: nextUnreadDMs,
            unreadTeams: nextUnreadTeams,
            unreadStandups: nextUnreadStandups,
            unreadTeamsCount: state.unreadTeamsCount + addTeams,
            unreadStandupsCount: state.unreadStandupsCount + addStandups,
          };
        });
      }

      // Reconnect the realtime channel
      const existing = get().realtimeChannel;
      if (existing) supabase.removeChannel(existing);
      get().initializeEvents();
    } catch (err) {
      console.error('❌ [FeedStore] refreshAfterWake failed:', err);
    }
  },

  initializeEvents: async () => {
    set({ isLoading: true, error: null });
    ensureConversationViewCaches();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) {
        console.warn('initializeEvents: no companyId; skipping.');
        set({
          events: [],
          unreadCount: 0,
          unreadDirectMessages: {},
          unreadStandups: {},
          unreadTeams: {},
          unreadTeamsCount: 0,
          unreadStandupsCount: 0,
          isLoading: false,
        });
        return;
      }

      // ============================================
      // PERFORMANCE OPTIMIZATION: Pagination
      // ============================================
      // Strategy: Load events for the current user from the database.
      // Since we filter by userId, we need to fetch more events to account for
      // the fact that many events may not be for this user. We keep fetching
      // until we have at least 50 user events or exhaust the database.
      //
      // Initial load: Keep fetching pages until we have 50+ user events
      // Infinite scroll: Fetches next 200 events older than the oldest currently loaded
      // ============================================
      const FEED_PAGE_SIZE = 200; // Fetch 200 events per page
      const TARGET_USER_EVENTS = 100; // Target ~100 events for the user after filtering (increased from 50)
      
      const allMappedEvents: FeedEvent[] = [];
      let lastCreatedAt: string | null = null;
      let hasMore = true;
      
      // Keep fetching until we have enough user events or exhaust the database
      let userEventsCount = 0;
      while (userEventsCount < TARGET_USER_EVENTS && hasMore) {
        let query = supabase
          .from('feed_events')
          .select('*')
          .eq('company_id', currentUser.companyId)
          .order('created_at', { ascending: false })
          .limit(FEED_PAGE_SIZE);
        
        if (lastCreatedAt) {
          query = query.lt('created_at', lastCreatedAt);
        }
        
        const { data, error } = await query;
        
        if (error) throw error;
        
        const pageData = data ?? [];
        // Update hasMore: true if we got a full page, false if we got fewer
        hasMore = pageData.length === FEED_PAGE_SIZE;
        
        if (pageData.length > 0) {
          const mapped = pageData.map((row) => mapFeedEventFromDB(row as FeedEventRow));
          allMappedEvents.push(...mapped);
          lastCreatedAt = (pageData[pageData.length - 1] as FeedEventRow).created_at;
          
          // Count user events in this page to check if we have enough
          const pageUserEvents = mapped.filter((e) => isFeedEventForUser(e, currentUser.id));
          
          userEventsCount += pageUserEvents.length;
        } else {
          hasMore = false;
        }
        
        // Safety: Don't fetch more than 10 pages (2000 events) to prevent infinite loops
        // Increased from 5 pages to handle accounts with many events
        if (allMappedEvents.length >= FEED_PAGE_SIZE * 10) {
          hasMore = false;
          break;
        }
      }
      
      // After the loop, hasMore indicates if there are more events in the database
      // (regardless of whether they match the user filter)

      // Show only events for me (never my own outbound mentions/DMs addressed to others)
      const events = allMappedEvents.filter((e) => isFeedEventForUser(e, currentUser.id));

      let unreadCount = 0;
      const unreadDMs: Record<string, boolean> = {};
      const unreadStandupMap: Record<string, boolean> = {}; // Per-meeting tracking
      const unreadTeamMap: Record<string, boolean> = {}; // December 4, 2025: Per-team tracking
      let unreadTeamsCount = 0;
      let unreadStandupsCount = 0;

      events.forEach((e) => {
        // Standard notification counting: only count notifications where userId matches current user
        // This ensures consistency across all notification displays
        if (!isEventReadByUser(e, currentUser.id) && e.userId === currentUser.id) {
          unreadCount++;
          if (e.type === 'direct_message_received' && e.initiatorId) {
            unreadDMs[e.initiatorId] = true;
          }
          if (e.streamType === 'team_stream') {
            unreadTeamsCount++;
            // December 4, 2025: Track per-team unread (keyed by recipientStreamId which is team id)
            if (e.recipientStreamId) {
              unreadTeamMap[e.recipientStreamId] = true;
            }
          }
          if (e.streamType === 'standup_stream') {
            unreadStandupsCount++;
            // Track per-meeting unread (keyed by recipientStreamId)
            if (e.recipientStreamId) {
              unreadStandupMap[e.recipientStreamId] = true;
            }
          }
        }
      });

      console.log(`[FeedStore] Initial load complete: ${allMappedEvents.length} total events fetched, ${events.length} user events after filtering, hasMore: ${hasMore}`);

      const newestTimestamp = events.length > 0 ? events[0].createdAt : null;

      set({
        events,
        unreadCount,
        unreadDirectMessages: unreadDMs,
        unreadStandups: unreadStandupMap,
        unreadTeams: unreadTeamMap, // December 4, 2025: Per-team tracking
        unreadTeamsCount,
        unreadStandupsCount,
        isLoading: false,
        hasMoreEvents: hasMore,
        consecutiveEmptyLoads: 0, // Reset on initial load
        lastEventTimestamp: newestTimestamp, // Track for dormancy gap-filling
      });

      // Realtime
      const existing = get().realtimeChannel;
      if (existing) supabase.removeChannel(existing);

      const channel = supabase
        .channel('feed_events_channel')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'feed_events',
            filter: `company_id=eq.${currentUser.companyId}`,
          },
          (payload) => {
            const newEvent = mapFeedEventFromDB(payload.new as FeedEventRow);

            // Company-wide realtime: drop rows addressed to other people (e.g. my outbound
            // @mention of Jayanthi must not land in my inbox as "I mentioned you").
            if (!isFeedEventForUser(newEvent, currentUser.id)) return;

            set((state) => {
              if (state.events.some((e) => e.id === newEvent.id)) return state;

              const nextEvents = [newEvent, ...state.events];

              // Only update counters for events that are for the current user.
              // Skip sidebar dots when already viewing that conversation.
              const viewingDm =
                newEvent.type === 'direct_message_received' &&
                !!newEvent.initiatorId &&
                isActivelyViewingConversation('direct_message', newEvent.initiatorId);
              const viewingTeam =
                newEvent.streamType === 'team_stream' &&
                isActivelyViewingConversation('team_stream', newEvent.recipientStreamId);
              const viewingStandup =
                newEvent.streamType === 'standup_stream' &&
                isActivelyViewingConversation('standup_stream', newEvent.recipientStreamId);

              const nextUnreadDMs = { ...state.unreadDirectMessages };
              if (
                newEvent.type === 'direct_message_received' &&
                newEvent.userId === currentUser.id &&
                !isEventReadByUser(newEvent, currentUser.id) &&
                newEvent.initiatorId &&
                !viewingDm
              ) {
                nextUnreadDMs[newEvent.initiatorId] = true;
              }

              // Standard notification counting: only count notifications where userId matches current user
              const isForMe = newEvent.userId === currentUser.id;
              const isUnreadForMe = !isEventReadByUser(newEvent, currentUser.id) && isForMe;
              const viewingThisStream = viewingDm || viewingTeam || viewingStandup;
              const addUnread = isUnreadForMe && !viewingThisStream ? 1 : 0;
              let addTeams = 0;
              let addStandups = 0;
              const nextUnreadStandups = { ...state.unreadStandups };
              const nextUnreadTeams = { ...state.unreadTeams }; // December 4, 2025: Per-team tracking
              if (isUnreadForMe && !viewingThisStream) {
                if (newEvent.streamType === 'team_stream') {
                  addTeams = 1;
                  // December 4, 2025: Track per-team unread
                  if (newEvent.recipientStreamId) {
                    nextUnreadTeams[newEvent.recipientStreamId] = true;
                  }
                }
                if (newEvent.streamType === 'standup_stream') {
                  addStandups = 1;
                  // Track per-meeting unread
                  if (newEvent.recipientStreamId) {
                    nextUnreadStandups[newEvent.recipientStreamId] = true;
                  }
                }
              }

              const newState = {
                events: nextEvents,
                lastEventTimestamp: newEvent.createdAt || state.lastEventTimestamp,
                unreadCount: state.unreadCount + addUnread,
                unreadDirectMessages: nextUnreadDMs,
                unreadStandups: nextUnreadStandups,
                unreadTeams: nextUnreadTeams, // December 4, 2025: Per-team tracking
                unreadTeamsCount: state.unreadTeamsCount + addTeams,
                unreadStandupsCount: state.unreadStandupsCount + addStandups,
              };

              // Show announcement banner for announcements received via realtime
              if (newEvent.type === 'announcement_made' && newEvent.userId === currentUser.id) {
                const isUrgent = newEvent.description?.toLowerCase().includes('#urgent');
                const message = newEvent.description || 'New announcement';
                get().showAnnouncementBanner(
                  message,
                  isUrgent ? 'urgent' : 'announcement',
                  newEvent.initiatorId,
                );
              }

              return newState;
            });

            // Already in the room — mark read so comments.user_read_at stays clear
            // and indicators don't come back on re-init / wake refresh.
            if (
              newEvent.userId === currentUser.id &&
              !isEventReadByUser(newEvent, currentUser.id)
            ) {
              if (
                newEvent.type === 'direct_message_received' &&
                newEvent.initiatorId &&
                isActivelyViewingConversation('direct_message', newEvent.initiatorId)
              ) {
                void get().markDirectMessagesAsRead(newEvent.initiatorId);
              } else if (
                newEvent.streamType === 'team_stream' &&
                newEvent.recipientStreamId &&
                isActivelyViewingConversation('team_stream', newEvent.recipientStreamId)
              ) {
                void get().markStreamAsRead('team_stream', newEvent.recipientStreamId);
              } else if (
                newEvent.streamType === 'standup_stream' &&
                newEvent.recipientStreamId &&
                isActivelyViewingConversation('standup_stream', newEvent.recipientStreamId)
              ) {
                void get().markStreamAsRead('standup_stream', newEvent.recipientStreamId);
              }
            }

            // Browser notifications should fire on the recipient's client.
            // June 2026: notify on @mentions AND direct messages (people miss DMs
            // that don't @mention them). Broader stream traffic stays dot-only.
            // Skip when already viewing that conversation.
            const suppressBrowserNotify =
              (newEvent.type === 'direct_message_received' &&
                isActivelyViewingConversation('direct_message', newEvent.initiatorId)) ||
              (newEvent.streamType === 'team_stream' &&
                isActivelyViewingConversation('team_stream', newEvent.recipientStreamId)) ||
              (newEvent.streamType === 'standup_stream' &&
                isActivelyViewingConversation('standup_stream', newEvent.recipientStreamId));

            if (
              !suppressBrowserNotify &&
              (newEvent.type === 'comment_mentioned' ||
                newEvent.type === 'stream_message_mentioned' ||
                newEvent.type === 'direct_message_received') &&
              newEvent.userId === currentUser.id
            ) {
              get().showBrowserNotification(newEvent);
            }

            // June 2026: Heal the active conversation.
            // The comments realtime channel can silently miss an INSERT when the tab has
            // been idle (e.g. you're sitting in a DM but not typing), yet the feed_events
            // channel reliably delivers the @mention / DM notification. If this event targets
            // the stream the user is currently viewing, soft-refresh that stream so the new
            // message shows up live instead of requiring a manual page refresh.
            if (
              newEvent.userId === currentUser.id &&
              newEvent.streamType &&
              (newEvent.type === 'comment_mentioned' ||
                newEvent.type === 'stream_message_mentioned' ||
                newEvent.type === 'direct_message_received')
            ) {
              // Lazy import avoids a circular module dependency (streamStore imports feedStore).
              void import('./streamStore')
                .then(({ default: useStreamStore }) => {
                  const streamState = useStreamStore.getState();
                  // For DMs the active recipient is the OTHER user (the initiator of this event).
                  const targetRecipientId =
                    newEvent.streamType === 'direct_message'
                      ? newEvent.initiatorId || newEvent.recipientStreamId
                      : newEvent.recipientStreamId;

                  if (
                    streamState.activeStreamType === newEvent.streamType &&
                    streamState.activeRecipientStreamId === targetRecipientId
                  ) {
                    void streamState.refreshActiveStream();
                  }
                })
                .catch((e) => console.error('[FeedStore] Failed to heal active stream:', e));
            }
          }
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            const latest = get();
            const isCurrentChannel = latest.realtimeChannel === channel;
            if (isCurrentChannel && !latest.isLoading) {
              console.warn('⚠️ [FeedStore] Feed realtime channel unhealthy; reinitializing feed channel.', { status });
              set({ realtimeChannel: null });
              window.setTimeout(() => {
                const now = get();
                if (!now.realtimeChannel && !now.isLoading) {
                  void now.initializeEvents();
                }
              }, 1000);
            }
            return;
          }
        });

      set({ realtimeChannel: channel });
      
      // Initialize unread indicators from comments table
      await get().initializeUnreadIndicators();
    } catch (err) {
      console.error('Error loading feed events:', err);
      set({ error: 'Failed to load feed events', isLoading: false });
      throw err;
    }
  },

  // Calculate unread indicators from comments table (not feed events)
  // This ensures indicators work for ALL messages (humans and agents) without creating feed events
  // 
  // IMPORTANT: See docs/features/NOTIFICATION_SYSTEM_ARCHITECTURE.md for full documentation
  // Key principle: Indicators from comments table, feed events only for @mentions
  initializeUnreadIndicators: async () => {
    try {
      ensureConversationViewCaches();
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.id || !currentUser.companyId) {
        console.warn('initializeUnreadIndicators: No current user/companyId found. Skipping.');
        return;
      }

      // Clean up existing comments subscription
      const existing = get().commentsRealtimeChannel;
      if (existing) supabase.removeChannel(existing);

      // Query unread comments for indicators
      // For DMs: messages sent TO me (recipient_stream_id = my id, user_id != my id)
      // Only count comments that haven't been read (user_read_at IS NULL)
      const { data: unreadDMs, error: dmError } = await supabase
        .from('comments')
        .select('user_id, recipient_stream_id, stream_type, created_at')
        .eq('company_id', currentUser.companyId)
        .eq('stream_type', 'direct_message')
        .eq('recipient_stream_id', currentUser.id)
        .neq('user_id', currentUser.id)
        .is('user_read_at', null) // Only unread comments
        .order('created_at', { ascending: false })
        .limit(1000); // Get recent unread DMs

      if (dmError) {
        console.error('Error fetching unread DMs:', dmError);
      } else {
        // Build unreadDirectMessages map from comments
        const unreadDMsMap: Record<string, boolean> = {};
        const viewingSendersToClear: string[] = [];
        if (unreadDMs) {
          // Group by sender (user_id)
          unreadDMs.forEach((comment) => {
            if (comment.user_id && comment.user_id !== currentUser.id) {
              // Don't re-light a dot for a DM you're currently viewing
              if (isActivelyViewingConversation('direct_message', comment.user_id)) {
                viewingSendersToClear.push(comment.user_id);
                return;
              }
              unreadDMsMap[comment.user_id] = true;
            }
          });
        }

        set({
          // Replace (don't merge): merge kept stale sender keys true after mark-as-read.
          unreadDirectMessages: unreadDMsMap,
        });

        // Persist clear for any unread rows belonging to an open conversation
        for (const senderId of [...new Set(viewingSendersToClear)]) {
          void get().markDirectMessagesAsRead(senderId);
        }
      }

      // Set up realtime subscription on comments table for indicator updates
      const commentsChannel = supabase
        .channel('comments_indicators_channel')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'comments',
            filter: `company_id=eq.${currentUser.companyId}`,
          },
          (payload) => {
            const newComment = payload.new as {
              user_id?: string;
              recipient_stream_id?: string;
              stream_type?: string;
            };
            const commentUserId = newComment.user_id;
            const commentRecipientId = newComment.recipient_stream_id;
            const commentStreamType = newComment.stream_type;
            if (!commentUserId) return;

            // Only update indicators for messages sent TO the current user
            if (commentUserId === currentUser.id) return; // Don't show indicator for own messages

            // Already in the room — mark read, do not light the sidebar dot.
            if (
              commentStreamType === 'direct_message' &&
              commentRecipientId === currentUser.id &&
              isActivelyViewingConversation('direct_message', commentUserId)
            ) {
              void get().markDirectMessagesAsRead(commentUserId);
              return;
            }
            if (
              commentStreamType === 'team_stream' &&
              commentRecipientId &&
              isActivelyViewingConversation('team_stream', commentRecipientId)
            ) {
              void get().markStreamAsRead('team_stream', commentRecipientId);
              return;
            }
            if (
              commentStreamType === 'standup_stream' &&
              commentRecipientId &&
              isActivelyViewingConversation('standup_stream', commentRecipientId)
            ) {
              void get().markStreamAsRead('standup_stream', commentRecipientId);
              return;
            }

            set((state) => {
              const nextUnreadDMs = { ...state.unreadDirectMessages };
              const nextUnreadStandups = { ...state.unreadStandups };
              const nextUnreadTeams = { ...state.unreadTeams }; // December 4, 2025: Per-team tracking
              let nextTeams = state.unreadTeamsCount;
              let nextStandups = state.unreadStandupsCount;

              // Direct messages: update indicator for sender
              if (commentStreamType === 'direct_message' && commentRecipientId === currentUser.id) {
                nextUnreadDMs[commentUserId] = true;
              }
              // Team streams: track per-team + increment count
              else if (commentStreamType === 'team_stream') {
                nextTeams += 1;
                // December 4, 2025: Track per-team unread (keyed by team id)
                if (commentRecipientId) {
                  nextUnreadTeams[commentRecipientId] = true;
                }
              }
              // Standup streams: track per-meeting + increment count
              else if (commentStreamType === 'standup_stream') {
                nextStandups += 1;
                // Track per-meeting unread (keyed by recipientStreamId)
                if (commentRecipientId) {
                  nextUnreadStandups[commentRecipientId] = true;
                }
              }

              return {
                unreadDirectMessages: nextUnreadDMs,
                unreadStandups: nextUnreadStandups,
                unreadTeams: nextUnreadTeams, // December 4, 2025: Per-team tracking
                unreadTeamsCount: nextTeams,
                unreadStandupsCount: nextStandups,
              };
            });
          }
        )
        .subscribe();

      set({ commentsRealtimeChannel: commentsChannel });
      console.log('✅ Unread indicators initialized from comments table');
    } catch (error) {
      console.error('Error initializing unread indicators:', error);
    }
  },

  loadMoreEvents: async () => {
    const { events, isLoadingMore } = get();
    if (isLoadingMore || !get().hasMoreEvents) return;

    set({ isLoadingMore: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) {
        set({ isLoadingMore: false, hasMoreEvents: false });
        return;
      }

      // Get the oldest event's created_at timestamp to fetch events older than it
      const oldestEvent = events.length > 0 
        ? events.reduce((oldest, event) => {
            const oldestTime = new Date(oldest.createdAt).getTime();
            const eventTime = new Date(event.createdAt).getTime();
            return eventTime < oldestTime ? event : oldest;
          })
        : null;

      const FEED_PAGE_SIZE = 200; // Fetch 200 events to account for user filtering
      
      let query = supabase
        .from('feed_events')
        .select('*')
        .eq('company_id', currentUser.companyId)
        .order('created_at', { ascending: false })
        .limit(FEED_PAGE_SIZE);

      // If we have an oldest event, fetch events older than it
      if (oldestEvent) {
        query = query.lt('created_at', oldestEvent.createdAt);
      }

      const { data, error } = await query;

      if (error) throw error;

      const mapped = (data ?? []).map((row) => mapFeedEventFromDB(row as FeedEventRow));

      // Same inbox gate as initialize / realtime
      const newEvents = mapped.filter((e) => isFeedEventForUser(e, currentUser.id));

      // Merge with existing events, avoiding duplicates
      const existingIds = new Set(events.map(e => e.id));
      const uniqueNewEvents = newEvents.filter(e => !existingIds.has(e.id));
      const mergedEvents = [...events, ...uniqueNewEvents];

      // Update unread counts
      let additionalUnreadCount = 0;
      const additionalUnreadDMs: Record<string, boolean> = {};
      const additionalUnreadStandups: Record<string, boolean> = {}; // Per-meeting tracking
      const additionalUnreadTeams: Record<string, boolean> = {}; // December 4, 2025: Per-team tracking
      let additionalTeamsCount = 0;
      let additionalStandupsCount = 0;

      uniqueNewEvents.forEach((e) => {
        if (!isEventReadByUser(e, currentUser.id) && e.userId === currentUser.id) {
          additionalUnreadCount++;
          if (e.type === 'direct_message_received' && e.initiatorId) {
            additionalUnreadDMs[e.initiatorId] = true;
          }
          if (e.streamType === 'team_stream') {
            additionalTeamsCount++;
            // December 4, 2025: Track per-team unread
            if (e.recipientStreamId) {
              additionalUnreadTeams[e.recipientStreamId] = true;
            }
          }
          if (e.streamType === 'standup_stream') {
            additionalStandupsCount++;
            // Track per-meeting unread
            if (e.recipientStreamId) {
              additionalUnreadStandups[e.recipientStreamId] = true;
            }
          }
        }
      });

      // Determine if there are more events to load
      // If we got fewer than FEED_PAGE_SIZE events from DB, we've reached the end of the database
      const dbHasMore = (data ?? []).length === FEED_PAGE_SIZE;
      
      // Track consecutive empty loads (no new user events)
      const currentConsecutiveEmpty = uniqueNewEvents.length === 0 
        ? get().consecutiveEmptyLoads + 1 
        : 0;
      
      // Stop loading if:
      // 1. DB has no more events (got fewer than FEED_PAGE_SIZE), OR
      // 2. We've had 10 consecutive loads with no new user events (increased from 3)
      // This allows for more gaps in the data before giving up, which is important
      // for accounts with many events scattered throughout the database
      const hasMore = dbHasMore && currentConsecutiveEmpty < 10;

      console.log(`[FeedStore] Load more: Fetched ${(data ?? []).length} events, ${uniqueNewEvents.length} new user events, consecutiveEmpty: ${currentConsecutiveEmpty}, hasMore: ${hasMore}`);

      set({
        events: mergedEvents,
        unreadCount: get().unreadCount + additionalUnreadCount,
        unreadDirectMessages: { ...get().unreadDirectMessages, ...additionalUnreadDMs },
        unreadStandups: { ...get().unreadStandups, ...additionalUnreadStandups },
        unreadTeams: { ...get().unreadTeams, ...additionalUnreadTeams }, // December 4, 2025: Per-team tracking
        unreadTeamsCount: get().unreadTeamsCount + additionalTeamsCount,
        unreadStandupsCount: get().unreadStandupsCount + additionalStandupsCount,
        isLoadingMore: false,
        hasMoreEvents: hasMore,
        consecutiveEmptyLoads: currentConsecutiveEmpty,
      });
    } catch (error: unknown) {
      console.error('Error loading more events:', error);
      set({ error: error instanceof Error ? error.message : String(error), isLoadingMore: false });
    }
  },

  addEvent: async (event) => {
    const isAnnouncementEvent = event.type === 'announcement_made';
    const isUrgent = event.description?.toLowerCase().includes('#urgent') ?? false;
    const currentUser = useUserStore.getState().currentUser;

    console.log('📬 feedStore.addEvent called:', {
      type: event.type,
      isAnnouncementEvent,
      userId: event.userId,
      initiatorId: event.initiatorId,
      isSender: event.userId === event.initiatorId
    });

    if (!ALLOWED_FEED_EVENT_TYPES.has(event.type)) {
      console.log('⚠️ Event type not allowed:', event.type);
      return;
    }

    if (!isAnnouncementEvent && !isUrgent) {
      try {
        const initiator = event.initiatorId;
        const recipient = event.userId ?? null;

        // Sprint events and card update events can bypass broadcast suppression (they notify others)
        const isSprintEvent = event.type.startsWith('sprint_');
        const isCardUpdateEvent = event.type === 'task_updated' || event.type === 'idea_updated';

        // Suppress self-directed noise — but keep comment_added / comment_replied so the
        // author's feed card preview updates immediately (edge marks those read-at-create).
        // Also: time_tracking; group @mentions; guide_published to the publisher.
        const isGroupMention = event.metadata?.groupMention === true;
        const isGuideAudienceEvent = event.type === 'guide_published';
        const isSelfCommentTrail =
          event.type === 'comment_added' || event.type === 'comment_replied';
        if (
          initiator &&
          recipient &&
          initiator === recipient &&
          event.type !== 'time_tracking' &&
          !isGroupMention &&
          !isGuideAudienceEvent &&
          !isSelfCommentTrail
        ) {
          console.log('⚠️ Suppressing self-directed event (sender === recipient)');
          return; // self-directed
        }
        
        if (initiator && !recipient && currentUser && initiator === currentUser.id && !isSprintEvent && !isCardUpdateEvent) {
          console.log('⚠️ Suppressing self-broadcast event');
          return; // self-broadcast
        }

        if (
          (event.type === 'stream_message_sent' || event.type === 'direct_message_received') &&
          event.userId &&
          event.initiatorId &&
          event.userId === event.initiatorId
        ) {
          console.log('⚠️ Suppressing DM to self');
          return;
        }
      } catch {
        /* ignore */
      }
    } else {
      console.log('✅ Announcement/Urgent - skipping self-suppression checks');
    }

    set({ isLoading: true, error: null });
    try {
      if (!currentUser) throw new Error('No authenticated user');

      let sharedItemDetails = undefined;
      if (event.shared_item_id) {
        const si = useSharedItemStore.getState().sharedItems.find((i) => i.id === event.shared_item_id);
        if (si) {
          sharedItemDetails = {
            title: si.title,
            url: si.url,
            type: si.type,
            description: si.description,
            tags: si.tags,
          };
        }
      }

      const body = { ...event, companyId: currentUser.companyId || null, sharedItemDetails };
      console.log('📬 Calling create-feed-notification edge function with body:', {
        type: body.type,
        userId: body.userId,
        initiatorId: body.initiatorId,
        isSender: body.userId === body.initiatorId
      });
      
      const { data, error } = await supabase.functions.invoke('create-feed-notification', { body });
      
      console.log('📬 Edge function response:', { data, error });
      
      if (error) throw error;

      // Merge the created row into local state immediately so the feed card preview
      // does not wait on realtime (self comment trail + same-tab author path).
      const createdRow = data?.event as FeedEventRow | undefined;
      if (createdRow && !data?.suppressed) {
        const mapped = mapFeedEventFromDB(createdRow);
        if (isFeedEventForUser(mapped, currentUser.id)) {
          set((state) => {
            if (state.events.some((e) => e.id === mapped.id)) {
              return { isLoading: false };
            }
            return {
              events: [mapped, ...state.events],
              lastEventTimestamp: mapped.createdAt || state.lastEventTimestamp,
              isLoading: false,
            };
          });
        } else {
          set({ isLoading: false });
        }
      } else {
        set({ isLoading: false });
      }
      
      // Show announcement banner for announcements (only once per announcement, not per user notification)
      if (event.type === 'announcement_made') {
        const isUrgent = event.description?.toLowerCase().includes('#urgent');
        const message = event.description || 'New announcement';
        // Only show banner if this is the first announcement event (check if banner is already showing)
        const currentBanner = get().announcementBanner;
        if (!currentBanner || currentBanner.message !== message) {
          get().showAnnouncementBanner(message, isUrgent ? 'urgent' : 'announcement', event.initiatorId);
        }
      }
      
      // Show browser notification for @mentions and direct messages.
      // June 2026: matches the realtime path so DMs surface a pop-up too.
      if (
        (event.type === 'comment_mentioned' || event.type === 'direct_message_received') &&
        event.userId === currentUser.id
      ) {
        // Create a FeedEvent object for the notification using the event data
        // The event object already has all the necessary fields
        const notificationEvent: FeedEvent = {
          id: (createdRow?.id as string | undefined) || data?.id || `temp-${Date.now()}`,
          type: event.type,
          title: event.title || 'Mentioned in Currents',
          description: event.description || 'You were mentioned',
          userId: event.userId,
          initiatorId: event.initiatorId,
          createdAt: new Date().toISOString(),
          read: {},
          streamType: event.streamType,
          recipientStreamId: event.recipientStreamId,
          commentId: event.commentId,
        };
        get().showBrowserNotification(notificationEvent);
      }
    } catch (error) {
      console.error('Error adding feed event:', error);
      set({ error: 'Failed to add feed event', isLoading: false });
    }
  },

  markAsRead: async (eventId) => {
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) return;

      // Get current read status from database
      const { data: currentEvent, error: selectError } = await supabase
        .from('feed_events')
        .select('read')
        .eq('id', eventId)
        .single();

      if (selectError) {
        console.error('Error fetching event for markAsRead:', selectError);
        return;
      }

      // Update the read status by adding the current user's ID
      const updatedRead = { ...currentEvent.read, [currentUser.id]: true };
      
      const { error } = await supabase
        .from('feed_events')
        .update({ read: updatedRead })
        .eq('id', eventId);
        
      if (error) throw error;

      set((state) => {
        const target = state.events.find((e) => e.id === eventId);
        const wasUnread = !!target && !isEventReadByUser(target, currentUser.id);

        const nextDMs = { ...state.unreadDirectMessages };
        if (target?.type === 'direct_message_received' && target.initiatorId) {
          const stillUnread = state.events.some(
            (e) =>
              e.type === 'direct_message_received' &&
              e.initiatorId === target.initiatorId &&
              !isEventReadByUser(e, currentUser.id) &&
              e.id !== target.id
          );
          if (!stillUnread) delete nextDMs[target.initiatorId];
        }

        let nextTeams = state.unreadTeamsCount;
        let nextStandups = state.unreadStandupsCount;
        if (wasUnread && (target?.userId === currentUser.id || target?.userId === null)) {
          if (target.streamType === 'team_stream') nextTeams = Math.max(0, nextTeams - 1);
          if (target.streamType === 'standup_stream') nextStandups = Math.max(0, nextStandups - 1);
        }

        return {
          events: state.events.map((e) => 
            e.id === eventId 
              ? { ...e, read: { ...e.read, [currentUser.id]: true } } 
              : e
          ),
          unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          unreadDirectMessages: nextDMs,
          unreadTeamsCount: nextTeams,
          unreadStandupsCount: nextStandups,
        };
      });
    } catch (error) {
      console.error('Error marking event read:', error);
    }
  },

  markAllAsRead: async (cardId) => {
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) return;

      console.log('markAllAsRead: Starting with currentUser:', currentUser.id, 'cardId:', cardId);

      // Get the current filtered events that are visible in the Feed
      const { events } = get();
      const cards = useCardStore.getState().cards;
      const sprints = useSprintStore.getState().sprints;
      const roadmaps = useRoadmapStore.getState().roadmaps;
      const follows = useFollowStore.getState().follows;
      // July 2026 fix: selectedTeamId lives on teamStore; reading it off cardStore
      // returned undefined, so markAllAsRead ignored the team filter.
      const selectedTeamId = useTeamStore.getState().selectedTeamId;

      console.log('markAllAsRead: Total events in store:', events.length);
      console.log('markAllAsRead: Selected team ID:', selectedTeamId);

      // Apply the same filtering logic as getFilteredEvents
      const filteredEvents = get().getFilteredEvents('all', cards, sprints, roadmaps, follows, selectedTeamId);
      
      console.log('markAllAsRead: Filtered events count:', filteredEvents.length);
      
      // Only mark events as read that are:
      // 1. Currently visible in the Feed (filteredEvents)
      // 2. Not already read by the current user
      const unreadFilteredEvents = filteredEvents.filter(e => {
        const isRead = isEventReadByUser(e, currentUser.id);
        console.log(`Event ${e.id}: read=${isRead}, read object:`, e.read);
        return !isRead;
      });
      
      console.log('markAllAsRead: Found', unreadFilteredEvents.length, 'unread events to mark as read');
      console.log('markAllAsRead: Unread event IDs:', unreadFilteredEvents.map(e => e.id));

      // Update each unread filtered event individually
      for (const event of unreadFilteredEvents) {
        console.log('markAllAsRead: Updating event', event.id);
        
        // Get current read status
        const { data: currentEvent, error: selectError } = await supabase
          .from('feed_events')
          .select('read')
          .eq('id', event.id)
          .single();

        if (selectError) {
          console.error('Error fetching event', event.id, ':', selectError);
          continue;
        }

        // Update the read status by adding the user
        const updatedRead = { ...currentEvent.read, [currentUser.id]: true };
        
        const { error } = await supabase
          .from('feed_events')
          .update({ read: updatedRead })
          .eq('id', event.id);
        
        if (error) {
          console.error('Error updating event', event.id, ':', error);
        } else {
          console.log('markAllAsRead: Successfully updated event', event.id);
        }
      }
      
      console.log('markAllAsRead: Database update completed');

      // Update the local state directly instead of refetching everything
      console.log('markAllAsRead: Updating local state...');
      set((state) => ({
        events: state.events.map(event => {
          if (unreadFilteredEvents.some(e => e.id === event.id)) {
            // Mark this event as read for the current user
            return {
              ...event,
              read: { ...event.read, [currentUser.id]: true }
            };
          }
          return event;
        }),
        unreadCount: Math.max(0, state.unreadCount - unreadFilteredEvents.length)
      }));
      console.log('markAllAsRead: Local state updated');
      
    } catch (error) {
      console.error('Error marking all read:', error);
      set({ error: 'Failed to mark events as read. Please try again.' });
    }
  },

  // NEW: unified stream read API
  markStreamAsRead: async (streamType, recipientStreamId) => {
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      // Get all unread events for this stream.
      // DMs: feed rows store recipient_stream_id as ME and initiator_id as the other person
      // (same partner id RoomView / gutter / lens chips use). Match on initiator first.
      let unreadEvents: { id: string; read: Record<string, boolean> | null }[] | null = null;
      let fetchError: unknown = null;
      if (streamType === 'direct_message') {
        const res = await supabase
          .from('feed_events')
          .select('id, read')
          .eq('user_id', currentUser.id)
          .eq('stream_type', 'direct_message')
          .or(`initiator_id.eq.${recipientStreamId},recipient_stream_id.eq.${recipientStreamId}`);
        unreadEvents = res.data;
        fetchError = res.error;
      } else {
        const res = await supabase
          .from('feed_events')
          .select('id, read')
          .eq('user_id', currentUser.id)
          .eq('stream_type', streamType)
          .eq('recipient_stream_id', recipientStreamId);
        unreadEvents = res.data;
        fetchError = res.error;
      }

      if (fetchError) throw fetchError;

      // Update each event's read JSONB to include current user
      for (const event of unreadEvents || []) {
        const currentRead = event.read || {};
        // Only update if not already read
        if (!currentRead[currentUser.id]) {
          const updatedRead = { ...currentRead, [currentUser.id]: true };
          const { error } = await supabase
            .from('feed_events')
            .update({ read: updatedRead })
            .eq('id', event.id);
          
          if (error) {
            console.error(`Error updating event ${event.id}:`, error);
          }
        }
      }

      // Mark comments as read in the database (for persistent indicator clearing)
      // This ensures indicators stay cleared after page refresh
      const now = new Date().toISOString();
      
      if (streamType === 'direct_message') {
        // For DMs: mark all comments sent TO me FROM this user as read
        // recipientStreamId is the other user's ID (the person you're chatting with)
        // We need to mark messages where:
        // - recipient_stream_id = currentUser.id (messages TO me)
        // - user_id = recipientStreamId (messages FROM the other user)
        const { error: commentsError } = await supabase
          .from('comments')
          .update({ user_read_at: now })
          .eq('company_id', currentUser.companyId)
          .eq('stream_type', 'direct_message')
          .eq('recipient_stream_id', currentUser.id)
          .eq('user_id', recipientStreamId) // Messages FROM this user
          .is('user_read_at', null) // Only update unread ones
          .select('id'); // Return updated IDs to verify
        
        if (commentsError) {
          console.error('Error marking DM comments as read:', commentsError);
        }
      } else {
        // For team/standup/company streams: mark all comments in this stream as read
        const { error: commentsError } = await supabase
          .from('comments')
          .update({ user_read_at: now })
          .eq('company_id', currentUser.companyId)
          .eq('stream_type', streamType)
          .eq('recipient_stream_id', recipientStreamId)
          .neq('user_id', currentUser.id) // Not my own messages
          .is('user_read_at', null) // Only update unread ones
          .select('id'); // Return updated IDs to verify
        
        if (commentsError) {
          console.error('Error marking stream comments as read:', commentsError);
        }
      }

      // Clear indicators from comments table (for sidebar red dots)
      // This ensures indicators are cleared when opening a stream
      set((state) => {
        let nextUnread = state.unreadCount;
        let nextTeams = state.unreadTeamsCount;
        let nextStandups = state.unreadStandupsCount;
        const nextDMs = { ...state.unreadDirectMessages };
        const nextUnreadStandups = { ...state.unreadStandups };
        const nextUnreadTeams = { ...state.unreadTeams }; // December 4, 2025: Per-team tracking

        // Clear indicators based on stream type
        if (streamType === 'direct_message') {
          // Clear DM indicator for this user
          delete nextDMs[recipientStreamId];
        } else if (streamType === 'team_stream') {
          // December 4, 2025: Clear per-team indicator
          delete nextUnreadTeams[recipientStreamId];
          // Decrement count (not reset to 0, as there may be other unread teams)
          nextTeams = Math.max(0, nextTeams - 1);
        } else if (streamType === 'standup_stream') {
          // Clear per-meeting indicator
          delete nextUnreadStandups[recipientStreamId];
          // Decrement count (not reset to 0, as there may be other unread meetings)
          nextStandups = Math.max(0, nextStandups - 1);
        }

        const matchesStream = (e: FeedEvent) => {
          if (e.streamType !== streamType) return false;
          if (streamType === 'direct_message') {
            // Partner id is initiator on inbound DM notifications (recipient is me).
            return e.initiatorId === recipientStreamId || e.recipientStreamId === recipientStreamId;
          }
          return e.recipientStreamId === recipientStreamId;
        };

        const updated = state.events.map((e) => {
          const isTarget =
            !isEventReadByUser(e, currentUser.id) &&
            e.userId === currentUser.id &&
            matchesStream(e);

          if (isTarget) {
            nextUnread = Math.max(0, nextUnread - 1);
            if (e.streamType === 'team_stream') nextTeams = Math.max(0, nextTeams - 1);
            if (e.streamType === 'standup_stream') nextStandups = Math.max(0, nextStandups - 1);

            // If this was a DM event (rare but possible when streamType === 'direct_message'),
            // clear the per-sender dot map.
            if (e.type === 'direct_message_received' && e.initiatorId) {
              // Are there any other unread DMs from the same initiator?
              const stillUnreadFromSender = state.events.some(
                (x) =>
                  !isEventReadByUser(x, currentUser.id) &&
                  x.type === 'direct_message_received' &&
                  x.initiatorId === e.initiatorId &&
                  !matchesStream(x)
              );
              if (!stillUnreadFromSender) delete nextDMs[e.initiatorId];
            }

            return { ...e, read: { ...e.read, [currentUser.id]: true } };
          }
          return e;
        });

        return {
          events: updated,
          unreadCount: nextUnread,
          unreadDirectMessages: nextDMs,
          unreadStandups: nextUnreadStandups,
          unreadTeams: nextUnreadTeams, // December 4, 2025: Per-team tracking
          unreadTeamsCount: nextTeams,
          unreadStandupsCount: nextStandups,
        };
      });
    } catch (error) {
      console.error('Error marking stream read:', error);
    }
  },

  // Back-compat; now delegates to markStreamAsRead and also clears DM map for that user.
  markDirectMessagesAsRead: async (otherUserId: string) => {
    await get().markStreamAsRead('direct_message', otherUserId);
    // Extra safety: ensure the DM map is cleared for that user.
    set((state) => {
      const next = { ...state.unreadDirectMessages };
      delete next[otherUserId];
      return { unreadDirectMessages: next };
    });
  },

getFilteredEvents: (
  filter,
  cards,
  sprints,
  roadmaps,
  follows,
  selectedTeamId
) => {
  const { events } = get();
  const currentUser = useUserStore.getState().currentUser;
  const allUsers = useUserStore.getState().allUsers || [];
  if (!currentUser) return [];

  // FIRST: Filter to only show events meant for the current user (same as notification system)
  const userEvents = events.filter(event => event.userId === currentUser.id);

  // Prefer company-wide cards for artifact/team matching. FeedView may pass the
  // team-filtered `cards` slice; assignment events for cards outside that slice
  // (or with null teamId) must still resolve for Following / team checks.
  const allCardsForLookup =
    useCardStore.getState()._allCards?.length > 0
      ? useCardStore.getState()._allCards
      : cards;

  const { teams } = useTeamStore.getState();
  const userTeam = new Map<string, string | null | undefined>(
    allUsers.map((u) => [u.id, u.team ?? null]),
  );

  const isAssignmentEvent = (e: { type: string; title?: string; description?: string }) =>
    (e.type === 'task_updated' || e.type === 'idea_updated') &&
    (
      (typeof e.description === 'string' && e.description.toLowerCase().includes('assign')) ||
      (typeof e.title === 'string' && e.title.toLowerCase().includes('assign'))
    );

  return userEvents
    .filter((e) =>
      eventBelongsToSelectedTeam({
        event: e,
        selectedTeamId,
        currentUserId: currentUser.id,
        teams,
        cards: allCardsForLookup,
        sprints,
        roadmaps,
        senderTeamId: e.initiatorId ? (userTeam.get(e.initiatorId) ?? null) : null,
      }),
    )
    .filter((e) => {
      switch (filter) {
        case 'all':
          return true;
        case 'collaborative':
          return (
            e.type === 'comment_mentioned' ||
            e.type === 'mention' ||
            e.type === 'comment_added' ||
            e.type === 'comment_replied' ||
            e.type === 'direct_message_received' ||
            e.type === 'stream_message_sent' ||
            e.type === 'stream_message_mentioned' ||
            e.type === 'shared_item_created' ||
            isAssignmentEvent(e) ||
            e.type === 'approval_requested' ||
            e.type === 'approval_approved' ||
            e.type === 'approval_rejected' ||
            e.type === 'task_completed_by_assignee' ||
            e.type === 'card_converted'
          );
        case 'following':
          return eventMatchesFollows(e, follows, allCardsForLookup);
        default:
          return false;
      }
    });
},

getUnreadCountForFilter: (
  filter,
  cards,
  sprints,
  roadmaps,
  follows,
  selectedTeamId
) => {
  const currentUser = useUserStore.getState().currentUser;
  if (!currentUser) return 0;

  // Badge counts must use the same gates as the visible Feed list
  return unreadGroupCount(
    get().getFilteredEvents(filter, cards, sprints, roadmaps, follows, selectedTeamId),
    currentUser.id,
  );
},

// Announcement banner methods
showAnnouncementBanner: (message: string, type: 'announcement' | 'urgent', initiatorId?: string) => {
  set({
    announcementBanner: {
      message,
      type,
      isVisible: true,
      initiatorId,
    },
  });
},

hideAnnouncementBanner: () => {
  set({ announcementBanner: null });
},

/**
 * Show browser notification for @mention events.
 * We show even when the tab has focus: "focused" doesn't mean the user is looking (video call, other window, etc.);
 * showing maximizes chance they see the mention.
 */
showBrowserNotification: (event: FeedEvent) => {
  recordNotificationDebug({
    status: 'attempt',
    eventId: event.id,
    eventType: event.type,
    streamType: event.streamType,
    recipientStreamId: event.recipientStreamId,
  });
  // Check if browser supports notifications
  if (typeof window === 'undefined' || !('Notification' in window)) {
    recordNotificationDebug({
      status: 'unsupported',
      eventId: event.id,
      eventType: event.type,
    });
    return;
  }

  // Check permission
  if (Notification.permission === 'denied') {
    recordNotificationDebug({
      status: 'denied',
      eventId: event.id,
      eventType: event.type,
    });
    return; // User has denied, don't try again
  }

  // Request permission if not granted
  if (Notification.permission === 'default') {
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        // Permission granted, show notification (unless Web Push covers this device)
        showInTabNotificationUnlessPushed(event);
        recordNotificationDebug({
          status: 'granted-after-request',
          eventId: event.id,
          eventType: event.type,
        });
      } else {
        recordNotificationDebug({
          status: 'request-not-granted',
          eventId: event.id,
          eventType: event.type,
          details: permission,
        });
      }
    });
    return;
  }

  // Permission is granted, show notification (unless Web Push covers this device)
  if (Notification.permission === 'granted') {
    showInTabNotificationUnlessPushed(event);
    recordNotificationDebug({
      status: 'granted',
      eventId: event.id,
      eventType: event.type,
    });
  }
},
}));

/**
 * June 2026: Dedup guard. When this device has an active Web Push subscription,
 * the server delivers the OS notification through the service worker, so we skip
 * the in-tab Notification to avoid showing it twice. Falls back to the in-tab
 * notification when not subscribed (dev, permission-only, or push unsupported).
 */
function showInTabNotificationUnlessPushed(event: FeedEvent) {
  hasActivePushSubscription()
    .then((hasPush) => {
      if (hasPush) {
        recordNotificationDebug({
          status: 'suppressed-web-push',
          eventId: event.id,
          eventType: event.type,
        });
        return;
      }
      showNotificationForEvent(event);
    })
    .catch(() => showNotificationForEvent(event));
}

async function hasActivePushSubscription(): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

/**
 * Helper function to show browser notification for a feed event
 */
function showNotificationForEvent(event: FeedEvent) {
  let notification: Notification | null = null;
  try {
    const iconUrl = typeof window !== 'undefined' ? `${window.location.origin}/favicon.svg` : '/favicon.svg';
    // June 2026: fall back to DM-appropriate copy so direct messages don't read "Mentioned".
    const isDirectMessage = event.type === 'direct_message_received';
    const fallbackTitle = isDirectMessage ? 'New message in Currents' : 'Mentioned in Currents';
    const fallbackBody = isDirectMessage ? 'You have a new direct message' : 'You were mentioned';
    notification = new Notification(event.title || fallbackTitle, {
      body: event.description || fallbackBody,
      icon: iconUrl,
      tag: `mention-${event.id}`, // Prevent duplicate notifications
      requireInteraction: false,
      silent: false,
    });
    recordNotificationDebug({
      status: 'shown',
      eventId: event.id,
      eventType: event.type,
      title: event.title || 'Mentioned in Currents',
    });
  } catch (error) {
    recordNotificationDebug({
      status: 'error',
      eventId: event.id,
      eventType: event.type,
      details: error instanceof Error ? error.message : String(error),
    });
    return;
  }

  // Handle notification click - navigate to relevant stream
  notification.onclick = () => {
    window.focus();
    notification.close();
    window.location.href = twoHrefFromFeedEvent(event);
  };
}

type NotificationDebugEntry = {
  timestamp: string;
  status:
    | 'attempt'
    | 'unsupported'
    | 'denied'
    | 'granted'
    | 'granted-after-request'
    | 'request-not-granted'
    | 'shown'
    | 'suppressed-web-push'
    | 'error';
  eventId?: string;
  eventType?: string;
  streamType?: string | null;
  recipientStreamId?: string | null;
  title?: string;
  details?: string;
};

const NOTIFICATION_DEBUG_KEY = 'notificationDebugLog';

function recordNotificationDebug(entry: Omit<NotificationDebugEntry, 'timestamp'>) {
  if (typeof window === 'undefined') return;
  try {
    const existingRaw = localStorage.getItem(NOTIFICATION_DEBUG_KEY);
    const existing = existingRaw ? (JSON.parse(existingRaw) as NotificationDebugEntry[]) : [];
    const nextEntry: NotificationDebugEntry = {
      timestamp: new Date().toISOString(),
      ...entry,
    };
    const next = [nextEntry, ...existing].slice(0, 20);
    localStorage.setItem(NOTIFICATION_DEBUG_KEY, JSON.stringify(next));
  } catch (error) {
    console.warn('Failed to record notification debug entry:', error);
  }
}

// Tu Metrics - December 5, 2025
Object.assign(useFeedStore, { tuMetrics: {
  id: 'feed-store',
  tu1: 0.70,  // Large store (~1340 lines) managing notifications/feed
  tu2: 0.75,  // Complex but necessary notification logic
  tu3: 6000,  // Core notification system
  breakageResistance: 1.5,  // Notification timing is critical
  notes: 'Oct 2026: self comment_added/comment_replied trail allowed (read-at-create) so ' +
         'feed card previews update for the author; other self-directed events still suppressed. ' +
         'addEvent merges edge-created rows into local state immediately.',
  lastRefactor: '2026-10-13'
}});

export default useFeedStore;

